import { AlertTriangle, ArrowRight, Check, CheckCircle2, XCircle } from 'lucide-react';
import type { MetaAdDraftInput } from '../../../types/metaAdDraft';
import type { MetaAdValidationIssue, ValidationResult } from '../../../lib/metaAdValidation';
import { AdMockup } from './AdMockup';
import { buildPrePublishChecklist } from '../prePublishChecklist';
import { scrollToValidationField } from '../previewNav';

// Stage 7A D1 — the Preview section content. Presentational: validation state is
// computed in MetaAdCompose (single source of truth, shared with the Publish
// button) and passed in. Multi-ad-set: the reach estimate moved to each ad set's
// card (per-ad-set targeting); the Preview section renders, in order:
//   1. ad mockup          (gated: only when validation passes) — the shared creative
//   2. validation summary (errors / warnings / ready)
//   3. pre-publish checklist

function IssueRow({ issue }: { issue: MetaAdValidationIssue }) {
  const isError = issue.severity === 'error';
  return (
    <li className="flex items-start justify-between gap-3">
      <span className={isError ? 'text-red-700' : 'text-amber-700'}>{issue.message}</span>
      <button
        type="button"
        onClick={() => scrollToValidationField(issue.field)}
        className="inline-flex shrink-0 items-center gap-0.5 text-xs font-medium text-slate-500 hover:text-slate-800"
      >
        Jump to field <ArrowRight className="h-3 w-3" />
      </button>
    </li>
  );
}

export function PreviewPanel({
  input,
  validation,
  pageName,
}: {
  input: MetaAdDraftInput;
  validation: ValidationResult;
  pageName: string | null;
}) {
  const { ok, errors, warnings } = validation;
  const checklist = buildPrePublishChecklist(input);

  return (
    <section id="preview" className="space-y-8 scroll-mt-6">
      <h2 className="text-lg font-semibold text-slate-900">Preview</h2>

      {/* Gate banner when there are blocking errors */}
      {!ok && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3">
          <p className="flex items-center gap-2 text-sm font-semibold text-red-800">
            <XCircle className="h-4 w-4" /> Resolve errors below before previewing
          </p>
          <ul className="mt-2 space-y-1.5 text-sm">
            {errors.map((e) => (
              <IssueRow key={e.code + e.field} issue={e} />
            ))}
          </ul>
        </div>
      )}

      {/* Mockup — only once validation passes */}
      {ok && (
        <div>
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
            Ad preview
          </h3>
          <AdMockup input={input} pageName={pageName} />
        </div>
      )}

      {/* Validation summary */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Validation</h3>
        {ok && warnings.length === 0 ? (
          <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm font-medium text-green-800">
            <CheckCircle2 className="h-4 w-4" /> Ready to publish
          </div>
        ) : (
          <div className="space-y-3">
            {ok && errors.length === 0 && (
              <div className="flex items-center gap-2 text-sm font-medium text-green-700">
                <CheckCircle2 className="h-4 w-4" /> No blocking errors
              </div>
            )}
            {warnings.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
                <p className="flex items-center gap-2 text-sm font-semibold text-amber-800">
                  <AlertTriangle className="h-4 w-4" /> Warnings ({warnings.length})
                </p>
                <ul className="mt-2 space-y-1.5 text-sm">
                  {warnings.map((w) => (
                    <IssueRow key={w.code + w.field} issue={w} />
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pre-publish checklist */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Pre-publish checklist
        </h3>
        <div className="rounded-md border border-slate-200 p-4">
          <ul className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {checklist.map((item) => (
              <li
                key={item.key}
                className={[
                  'flex items-center gap-2 text-sm',
                  item.done ? 'text-slate-700' : 'text-slate-400',
                ].join(' ')}
              >
                <span
                  className={[
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-full',
                    item.done ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-300',
                  ].join(' ')}
                >
                  {item.done && <Check className="h-3 w-3" />}
                </span>
                <span className="flex-1">{item.label}</span>
                {item.detail && (
                  <span className="text-xs text-slate-400">{item.detail}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
